<template>
  <div class="oee-root">

    <!-- Filtros -->
    <div class="oee-filters">
      <div class="oee-filters__row">
        <v-autocomplete v-model="filtros.maquinas" :items="instancias" :item-text="nombreMaquina"
          item-value="idMaquinaIns" label="Máquinas" multiple chips small-chips deletable-chips clearable dense outlined
          hide-details :loading="loadingInstancias" :disabled="loading" class="oee-field oee-field--grow" />
        <VueCtkDateTimePicker id="oee-inicio" v-model="filtros.inicio" label="Inicio" format="YYYY-MM-DD HH:mm"
          formatted="YYYY-MM-DD HH:mm" noHeader overlay buttonNowTranslation="Ahora" :disabled="loading"
          class="oee-field" />
        <VueCtkDateTimePicker id="oee-fin" v-model="filtros.fin" :min-date="filtros.inicio" label="Fin"
          format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" noHeader overlay buttonNowTranslation="Ahora"
          :disabled="loading" class="oee-field" />
        <v-btn class="oee-go" depressed color="primary" dark :loading="loading" :disabled="loading || !puedeConsultar"
          @click="consultar">
          Consultar
        </v-btn>
      </div>

      <div class="oee-quick">
        <v-btn v-for="q in atajos" :key="q.key" small rounded color="primary" :outlined="atajoActivo !== q.key"
          :depressed="atajoActivo === q.key" class="oee-quick__btn text-none" @click="aplicarAtajo(q.key)">
          {{ q.label }}
        </v-btn>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="#111827" height="2" class="oee-loader" />

    <!-- Estados -->
    <div v-if="!consultado && !loading" class="oee-state">
      Selecciona el periodo y consulta para ver el OEE.
    </div>
    <div v-else-if="consultado && !loading && !maquinas.length" class="oee-state">
      Sin datos para el rango seleccionado.
    </div>

    <!-- Resultados -->
    <template v-if="maquinas.length && !loading">

      <!-- KPIs -->
      <div class="oee-kpis">
        <div v-for="kpi in kpisCards" :key="kpi.key" class="oee-kpi" :style="{ '--kpi-color': colorKpi(kpi) }">
          <span class="oee-kpi__label">
            <span class="oee-kpi__dot"></span>
            {{ kpi.label }}
          </span>
          <span class="oee-kpi__value" :style="{ color: colorKpi(kpi) }">{{ pf(kpisGlobales[kpi.key]) }}</span>
        </div>
      </div>

      <!-- Comparativa OEE: clic en una barra abre el detalle de esa máquina -->
      <div v-if="maquinasActivas.length > 1" class="oee-chart">
        <div class="oee-chart__head">
          <span>OEE por máquina</span>
          <span class="oee-chart__hint">Toca una barra para ver su detalle</span>
        </div>
        <VueApexCharts type="bar" :height="70 + maquinasActivas.length * 42" :options="comparativaOptions"
          :series="comparativaSeries" />
      </div>

      <!-- Lista de máquinas -->
      <div class="oee-list">
        <div class="oee-list__head">
          <span class="c-name">Máquina</span>
          <span class="c-oee">OEE</span>
          <span class="c-met">Disp.</span>
          <span class="c-met">Rend.</span>
          <span class="c-met">Calidad</span>
          <span class="c-met">No OK</span>
          <span class="c-pz">Piezas</span>
        </div>

        <v-expansion-panels v-model="panelesAbiertos" multiple flat accordion class="oee-panels">
          <v-expansion-panel v-for="maq in maquinasActivas" :key="maq.idMaquinaIns" ref="panels">
            <v-expansion-panel-header class="oee-row" hide-actions>
              <div class="oee-row__grid">
                <span class="c-name">
                  <v-icon size="16" class="oee-row__chev">mdi-chevron-right</v-icon>
                  {{ maq.nombre }}
                </span>
                <span class="c-oee">
                  <span class="oee-row__oee" :style="{ color: colorOee(maq.oee) }">{{ pf(maq.oee) }}</span>
                  <span class="oee-row__bar">
                    <span class="oee-row__bar-fill"
                      :style="{ width: barWidth(maq.oee), background: colorOee(maq.oee) }"></span>
                  </span>
                </span>
                <span class="c-met">{{ pf(maq.disponibilidad) }}</span>
                <span class="c-met">{{ pf(maq.rendimiento) }}</span>
                <span class="c-met">{{ pf(maq.calidad) }}</span>
                <span class="c-met oee-nook" :class="{ 'has-nook': maq.piezasNoOk > 0 }">{{ nf(maq.piezasNoOk) }}</span>
                <span class="c-pz">{{ nf(maq.piezasHechas) }} <i>/ {{ nf(maq.piezasEsperadas) }}</i></span>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="oee-row__content">
              <table v-if="maq.operaciones.length" class="oee-detalle">
                <thead>
                  <tr>
                    <th class="t-l">ODT</th>
                    <th class="t-l">Artículo</th>
                    <th class="t-l">Molde</th>
                    <th class="t-r">Rate</th>
                    <th>OEE</th>
                    <th>Disp.</th>
                    <th>Rend.</th>
                    <th>Calidad</th>
                    <th class="t-r">No OK</th>
                    <th class="t-r">Hechas</th>
                    <th class="t-r">Esperadas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(op, i) in maq.operaciones" :key="i">
                    <td class="t-l">{{ op.clave }}</td>
                    <td class="t-l">{{ op.articulo }}</td>
                    <td class="t-l">{{ op.molde }}</td>
                    <td class="t-r">{{ op.rateTxt }}</td>
                    <td :style="{ color: colorOee(op.oee), fontWeight: 600 }">{{ pf(op.oee) }}</td>
                    <td>{{ pf(op.disponibilidad) }}</td>
                    <td>{{ pf(op.rendimiento) }}</td>
                    <td>{{ pf(op.calidad) }}</td>
                    <td class="t-r oee-nook" :class="{ 'has-nook': op.piezasNoOk > 0 }">{{ nf(op.piezasNoOk) }}</td>
                    <td class="t-r">{{ nf(op.piezasHechas) }}</td>
                    <td class="t-r">{{ nf(op.piezasEsperadas) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="oee-detalle-empty">
                Sin ODTs ni operaciones en el rango.
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Máquinas sin actividad en el rango (no afectan OEE ni gráfica) -->
        <div v-for="maq in maquinasSinActividad" :key="'idle-' + maq.idMaquinaIns" class="oee-idle-row">
          <span class="oee-idle-name">{{ maq.nombre }}</span>
          <span class="oee-idle-tag">Sin actividad</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

/* Paleta y umbrales de color por OEE (en %).
   Verde:  OEE >= 85
   Ámbar:  65 <= OEE < 85
   Rojo:   OEE < 65                                */
const OEE_UMBRAL_VERDE = 85
const OEE_UMBRAL_AMBAR = 65
const OEE_VERDE = '#16A34A'
const OEE_AMBAR = '#F59E0B'
const OEE_ROJO = '#EF4444'

export default {
  name: 'ReporteDetalleOEEMaquinaV2',
  components: { VueApexCharts },

  props: {
    active: { type: Boolean, default: false },
  },

  data: () => ({
    instancias: [],
    loadingInstancias: false,
    loading: false,
    consultado: false,

    filtros: {
      maquinas: [],
      inicio: null,
      fin: null,
    },
    rangoConsultado: { inicio: null, fin: null },
    atajoActivo: 'hoy',

    maquinas: [],
    panelesAbiertos: [],

    atajos: [
      { key: 'hoy', label: 'Hoy' },
      { key: 'ayer', label: 'Ayer' },
      { key: '7d', label: '7 días' },
      { key: '30d', label: '30 días' },
    ],

    kpisCards: [
      { key: 'oee', label: 'OEE', color: null },
      { key: 'disponibilidad', label: 'Disponibilidad', color: '#2563EB' },
      { key: 'rendimiento', label: 'Eficiencia', color: '#7C3AED' },
      { key: 'calidad', label: 'Calidad', color: '#0D9488' },
    ],
  }),

  computed: {
    ...mapState({ nombreEmpresa: 'nombreEmpresa' }),

    isNYX() {
      return (this.nombreEmpresa || '').toLowerCase() === 'nyx'
    },

    puedeConsultar() {
      return !!this.filtros.inicio && !!this.filtros.fin
    },

    maquinasActivas() {
      return this.maquinas
        .filter(m => m.aplica)
        .sort((a, b) => (b.oee || 0) - (a.oee || 0))
    },

    maquinasSinActividad() {
      return this.maquinas
        .filter(m => !m.aplica)
        .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es', { numeric: true }))
    },

    kpisGlobales() {
      // Promedio ponderado por piezas esperadas (cae a promedio simple si no hay esperadas).
      // Sólo máquinas con actividad: las "sin actividad" no deben afectar el OEE.
      let pesoTotal = 0
      let sumD = 0, sumR = 0, sumC = 0, sumO = 0
      let n = 0, sD = 0, sR = 0, sC = 0, sO = 0

      this.maquinasActivas.forEach(m => {
        const peso = Number(m.piezasEsperadas) || 0
        if (peso > 0) {
          pesoTotal += peso
          sumD += (m.disponibilidad || 0) * peso
          sumR += (m.rendimiento || 0) * peso
          sumC += (m.calidad || 0) * peso
          sumO += (m.oee || 0) * peso
        }
        n++
        sD += m.disponibilidad || 0
        sR += m.rendimiento || 0
        sC += m.calidad || 0
        sO += m.oee || 0
      })

      if (pesoTotal > 0) {
        return {
          disponibilidad: sumD / pesoTotal,
          rendimiento: sumR / pesoTotal,
          calidad: sumC / pesoTotal,
          oee: sumO / pesoTotal,
        }
      }
      if (!n) return { disponibilidad: 0, rendimiento: 0, calidad: 0, oee: 0 }
      return {
        disponibilidad: sD / n,
        rendimiento: sR / n,
        calidad: sC / n,
        oee: sO / n,
      }
    },

    comparativaSeries() {
      return [{ name: 'OEE', data: this.maquinasActivas.map(m => Number((m.oee || 0).toFixed(1))) }]
    },

    comparativaMax() {
      const vals = this.maquinasActivas.map(m => Number(m.oee) || 0)
      const mx = vals.length ? Math.max(...vals) : 100
      if (mx <= 100) return 100
      return Math.ceil((mx + 5) / 10) * 10
    },

    comparativaOptions() {
      const colors = this.maquinasActivas.map(m => this.colorOee(m.oee))
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'Roboto, sans-serif',
          animations: { enabled: true, easing: 'easeinout', speed: 450 },
          events: {
            dataPointSelection: (e, ctx, cfg) => this.seleccionarBarra(cfg.dataPointIndex),
          },
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 5, borderRadiusApplication: 'end', barHeight: '60%', distributed: true },
        },
        colors,
        states: {
          hover: { filter: { type: 'darken', value: 0.9 } },
          active: { filter: { type: 'none' } },
        },
        dataLabels: {
          enabled: true,
          formatter: v => (v == null ? '' : `${Number(v).toFixed(1)}%`),
          style: { fontSize: '15px', fontWeight: 700, colors: ['#1F2937'] },
          background: { enabled: false },
          offsetX: 36,
        },
        legend: { show: false },
        grid: {
          borderColor: '#F0F1F4',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
        },
        xaxis: {
          categories: this.maquinasActivas.map(m => m.nombre),
          min: 0,
          max: this.comparativaMax,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { fontWeight: 700, colors: '#374151', fontSize: '13px' } } },
        tooltip: { y: { formatter: v => (v == null ? '' : `${Number(v).toFixed(1)}%`) } },
      }
    },
  },

  mounted() {
    this.getInstancias()
    this.aplicarAtajo('hoy')
  },

  methods: {
    /* ── helpers ── */
    val(obj, ...keys) {
      for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k]
      }
      return undefined
    },
    arr(v) { return Array.isArray(v) ? v : [] },
    num(v, fb = 0) {
      const n = Number(v)
      return Number.isFinite(n) ? n : fb
    },
    pf(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `${n.toFixed(1)}%`
    },
    nf(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('es-MX')
    },
    barWidth(v) {
      const n = Math.max(0, Math.min(100, Number(v) || 0))
      return `${n}%`
    },

    seleccionarBarra(idx) {
      if (idx == null || idx < 0 || idx >= this.maquinasActivas.length) return
      if (!this.panelesAbiertos.includes(idx)) this.panelesAbiertos.push(idx)
      this.$nextTick(() => {
        const panel = this.$refs.panels && this.$refs.panels[idx]
        const el = panel && panel.$el ? panel.$el : panel
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    colorOee(v) {
      const n = Number(v) || 0
      if (n >= OEE_UMBRAL_VERDE) return OEE_VERDE
      if (n >= OEE_UMBRAL_AMBAR) return OEE_AMBAR
      return OEE_ROJO
    },
    colorKpi(kpi) {
      // El OEE usa el color por umbral; los factores tienen su color fijo
      return kpi.key === 'oee' ? this.colorOee(this.kpisGlobales.oee) : kpi.color
    },

    nombreMaquina(maquina) {
      if (!maquina) return ''
      const clave = maquina.clave || maquina.nombre || maquina.idMaquinaIns
      const marca = maquina.marca || ''
      const modelo = maquina.modelo || ''
      const extra = `${marca} ${modelo}`.trim()
      return extra ? `[${clave}] - ${extra}` : `[${clave}]`
    },

    pad(n) { return String(n).padStart(2, '0') },

    formatoFecha(d) {
      const f = `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
      // formato 'YYYY-MM-DD HH:mm' que usa VueCtkDateTimePicker
      return `${f} ${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`
    },

    aplicarAtajo(tipo) {
      this.atajoActivo = tipo
      const ahora = new Date()
      const inicio = new Date(ahora)
      const fin = new Date(ahora)

      if (tipo === 'hoy') {
        inicio.setHours(0, 0, 0, 0)
      } else if (tipo === 'ayer') {
        inicio.setDate(inicio.getDate() - 1)
        inicio.setHours(0, 0, 0, 0)
        fin.setDate(fin.getDate() - 1)
        fin.setHours(23, 59, 0, 0)
      } else if (tipo === '7d') {
        inicio.setDate(inicio.getDate() - 6)
        inicio.setHours(0, 0, 0, 0)
      } else if (tipo === '30d') {
        inicio.setDate(inicio.getDate() - 29)
        inicio.setHours(0, 0, 0, 0)
      }

      this.filtros.inicio = this.formatoFecha(inicio)
      this.filtros.fin = this.formatoFecha(fin)
    },

    /* ── data ── */
    getInstancias() {
      this.loadingInstancias = true
      axios.get('/MaquinaInstancias')
        .then(res => { this.instancias = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.instancias = [] })
        .finally(() => { this.loadingInstancias = false })
    },

    aFechaEndpoint(v) {
      // MxDateTimePicker entrega 'YYYY-MM-DD HH:mm'; el EP acepta segundos
      if (!v) return null
      const s = String(v).trim().replace('T', ' ')
      return s.length === 16 ? `${s}:00` : s
    },

    construirQuery(ids, inicio, fin) {
      const params = new URLSearchParams()
      ids.forEach(id => params.append('idsMaquinasIns', id))
      params.append('Inicio', inicio)
      params.append('Fin', fin)
      params.append('calcularPorHoras', false)
      if (this.isNYX) params.append('ParosNoProgAfectaEsperadas', false)
      return params.toString()
    },

    async consultar() {
      if (this.loading || !this.puedeConsultar) return

      let ids = this.arr(this.filtros.maquinas).filter(Boolean)
      if (!ids.length) ids = this.instancias.map(m => m.idMaquinaIns).filter(Boolean)
      if (!ids.length) return

      const inicio = this.aFechaEndpoint(this.filtros.inicio)
      const fin = this.aFechaEndpoint(this.filtros.fin)

      this.loading = true
      this.consultado = true
      try {
        const query = this.construirQuery(ids, inicio, fin)
        const { data } = await axios.get(`/OEEV2/ReporteOEEMultiplesMaquinas?${query}`)
        const lista = Array.isArray(data) ? data : []

        this.maquinas = lista.map(m => this.adaptarMaquina(m))
        this.panelesAbiertos = this.maquinasActivas.length === 1 ? [0] : []
        this.rangoConsultado = {
          inicio: this.filtros.inicio,
          fin: this.filtros.fin,
        }
      } catch (e) {
        console.error('Error al consultar ReporteOEEMultiplesMaquinas:', e)
        this.maquinas = []
      } finally {
        this.loading = false
      }
    },

    adaptarMaquina(m) {
      const oee = this.val(m, 'oeeCalculado', 'OEECalculado') || {}
      const odts = this.arr(this.val(m, 'odTs', 'odts', 'ODTs'))

      const operaciones = []
      odts.forEach(odt => {
        const clave = this.val(odt, 'claveTrabajoOrden', 'ClaveTrabajoOrden', 'clave', 'Clave') || '—'
        this.arr(this.val(odt, 'operaciones', 'Operaciones')).forEach(op => {
          const opOee = this.val(op, 'oeeCalculado', 'OEECalculado') || {}
          // Rate programado en pz/h, calculado por el backend
          const rate = this.num(this.val(op, 'rate', 'Rate'), 0)
          operaciones.push({
            clave,
            articulo: this.val(op, 'codigoArticulo', 'CodigoArticulo')
              || this.val(op, 'nombreArticulo', 'NombreArticulo') || '—',
            molde: this.val(op, 'codigoMolde', 'CodigoMolde', 'nombreMolde', 'NombreMolde')
              || this.val(odt, 'codigoMolde', 'CodigoMolde', 'nombreMolde', 'NombreMolde') || '—',
            rate,
            rateTxt: rate > 0 ? `${this.nf(rate)} pz/h` : '—',
            oee: this.num(this.val(opOee, 'oee', 'OEE'), null),
            disponibilidad: this.num(this.val(opOee, 'disponibilidad', 'Disponibilidad'), null),
            rendimiento: this.num(this.val(opOee, 'rendimiento', 'Eficiencia'), null),
            calidad: this.num(this.val(opOee, 'calidad', 'Calidad'), null),
            piezasOk: this.num(this.val(opOee, 'piezasOk', 'PiezasOk')
              ?? this.val(op, 'piezasOk', 'PiezasOk')),
            piezasNoOk: this.num(this.val(opOee, 'piezasNoOk', 'PiezasNoOk')
              ?? this.val(op, 'piezasNoOk', 'PiezasNoOk')),
            piezasHechas: this.num(this.val(opOee, 'piezasHechas', 'PiezasHechas')
              ?? this.val(op, 'piezasHechas', 'PiezasHechas')),
            piezasEsperadas: this.num(this.val(opOee, 'piezasEsperadas', 'PiezasEsperadas')
              ?? this.val(op, 'piezasEsperadas', 'PiezasEsperadas')),
          })
        })
      })

      return {
        idMaquinaIns: this.val(m, 'idMaquinaIns', 'IdMaquinaIns'),
        nombre: this.val(m, 'nombreMaquina', 'NombreMaquina')
          || this.val(m, 'nombreInstancia', 'NombreInstancia') || 'Máquina',
        // false sólo cuando el back marca "no aplica OEE" (sin actividad en el rango)
        aplica: this.val(oee, 'aplicaOEE', 'AplicaOEE') !== false,
        oee: this.num(this.val(oee, 'oee', 'OEE'), 0),
        disponibilidad: this.num(this.val(oee, 'disponibilidad', 'Disponibilidad'), 0),
        rendimiento: this.num(this.val(oee, 'rendimiento', 'Eficiencia'), 0),
        calidad: this.num(this.val(oee, 'calidad', 'Calidad'), 0),
        piezasOk: this.num(this.val(oee, 'piezasOk', 'PiezasOk'), 0),
        piezasNoOk: this.num(this.val(oee, 'piezasNoOk', 'PiezasNoOk'), 0),
        piezasHechas: this.num(this.val(oee, 'piezasHechas', 'PiezasHechas'), 0),
        piezasEsperadas: this.num(this.val(oee, 'piezasEsperadas', 'PiezasEsperadas'), 0),
        operaciones,
      }
    },
  },
}
</script>

<style scoped>
.oee-root {
  font-family: 'Roboto', -apple-system, sans-serif;
  padding: 8px 4px 24px;
}

/* Filtros */
.oee-filters {
  margin-bottom: 22px;
}

.oee-filters__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
}

.oee-field {
  flex: 0 0 230px;
}

.oee-field--grow {
  flex: 1 1 280px;
}

.oee-field ::v-deep .v-input__slot {
  border-radius: 10px;
  min-height: 42px;
}

.oee-field ::v-deep fieldset {
  border-color: #E5E7EB;
}

/* VueCtkDateTimePicker */
.oee-field ::v-deep .field-input {
  min-height: 42px !important;
  height: 42px !important;
  border-radius: 10px !important;
  border-color: #E5E7EB !important;
  font-size: 13.5px;
}

.oee-go {
  height: 42px !important;
  border-radius: 10px !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  box-shadow: none !important;
  padding: 0 22px !important;
}

.oee-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.oee-quick__btn {
  letter-spacing: 0;
}

.oee-loader {
  margin-bottom: 16px;
  border-radius: 2px;
}

/* Estados */
.oee-state {
  padding: 56px 16px;
  text-align: center;
  color: #9CA3AF;
  font-size: 14px;
}

/* KPIs */
.oee-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border: 1px solid #EEF0F3;
  border-radius: 14px;
  padding: 6px 0;
  margin-bottom: 26px;
}

.oee-kpi {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 22px;
  border-left: 1px solid #EEF0F3;
}

.oee-kpi:first-child {
  border-left: none;
}

.oee-kpi__label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6B7280;
  font-weight: 600;
}

.oee-kpi__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--kpi-color);
  flex-shrink: 0;
}

.oee-kpi__value {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1;
}

/* Gráfica comparativa */
.oee-chart {
  border: 1px solid #EEF0F3;
  border-radius: 14px;
  padding: 16px 18px 6px;
  margin-bottom: 18px;
}

.oee-chart__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
}

.oee-chart__head>span:first-child {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.oee-chart__hint {
  font-size: 11.5px;
  color: #9CA3AF;
}

.oee-chart ::v-deep .apexcharts-bar-series .apexcharts-bar-area {
  cursor: pointer;
}

/* Lista de máquinas */
.oee-list {
  border: 1px solid #EEF0F3;
  border-radius: 14px;
  overflow: hidden;
}

.oee-list__head,
.oee-row__grid {
  display: grid;
  grid-template-columns: minmax(190px, 260px) 132px 76px 76px 80px 80px 112px;
  align-items: center;
  gap: 12px;
}

.oee-list__head {
  padding: 12px 18px;
  background: #FAFBFC;
  border-bottom: 1px solid #EEF0F3;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9CA3AF;
  font-weight: 500;
}

.c-oee,
.c-met {
  text-align: right;
}

.c-pz {
  text-align: right;
}

.oee-list__head .c-name {
  padding-left: 24px;
}

/* filas (expansion panels aplanados) */
.oee-panels ::v-deep .v-expansion-panel {
  background: transparent;
}

.oee-panels ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.oee-panels ::v-deep .v-expansion-panel:not(:last-child) {
  border-bottom: 1px solid #F2F3F5;
}

.oee-panels ::v-deep .v-expansion-panel--active>.oee-row {
  background: #FAFBFC;
}

.oee-row {
  padding: 0 18px !important;
  min-height: 56px !important;
}

.oee-row ::v-deep .v-expansion-panel-header__icon {
  display: none;
}

.oee-row__grid {
  width: 100%;
}

.oee-row__chev {
  color: #C4C9D1 !important;
  transition: transform 0.2s ease;
  margin-right: 2px;
}

.oee-panels ::v-deep .v-expansion-panel--active .oee-row__chev {
  transform: rotate(90deg);
}

.c-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  font-weight: 500;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-met {
  font-size: 13px;
  color: #4B5563;
  font-variant-numeric: tabular-nums;
}

.c-pz {
  font-size: 13px;
  color: #111827;
  font-variant-numeric: tabular-nums;
}

.c-pz i {
  color: #9CA3AF;
  font-style: normal;
}

.oee-nook {
  color: #9CA3AF;
}

.oee-nook.has-nook {
  color: #EF4444;
  font-weight: 600;
}

.c-oee {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.oee-row__oee {
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  min-width: 48px;
  text-align: right;
}

.oee-row__bar {
  width: 56px;
  height: 5px;
  border-radius: 5px;
  background: #EEF0F3;
  overflow: hidden;
  flex-shrink: 0;
}

.oee-row__bar-fill {
  display: block;
  height: 100%;
  border-radius: 5px;
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

/* Detalle */
.oee-row__content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 18px 16px 24px;
}

.oee-detalle {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.oee-detalle thead th {
  text-align: right;
  padding: 8px 10px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #9CA3AF;
  font-weight: 500;
  border-bottom: 1px solid #EEF0F3;
}

.oee-detalle th.t-l,
.oee-detalle td.t-l {
  text-align: left;
}

.oee-detalle th.t-r,
.oee-detalle td.t-r {
  text-align: right;
}

.oee-detalle tbody td {
  text-align: right;
  padding: 9px 10px;
  color: #4B5563;
  border-bottom: 1px solid #F5F6F8;
  font-variant-numeric: tabular-nums;
}

.oee-detalle tbody tr:last-child td {
  border-bottom: none;
}

.oee-detalle tbody tr:hover td {
  background: #FAFBFC;
}

.oee-detalle td.t-l {
  color: #111827;
}

.oee-detalle-empty {
  padding: 16px 4px;
  font-size: 13px;
  color: #9CA3AF;
}

/* Filas sin actividad */
.oee-idle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  border-top: 1px solid #F2F3F5;
  background: #FCFCFD;
}

.oee-idle-name {
  font-size: 13.5px;
  font-weight: 500;
  color: #9CA3AF;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oee-idle-tag {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9CA3AF;
  background: #F0F1F4;
  border-radius: 20px;
  padding: 3px 11px;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 860px) {
  .oee-kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .oee-kpi:nth-child(3) {
    border-left: none;
  }

  .oee-list {
    border: none;
    border-radius: 0;
  }

  .oee-list__head {
    display: none;
  }

  .oee-row__grid {
    grid-template-columns: 1fr auto;
    grid-row-gap: 4px;
  }

  .c-met,
  .c-pz {
    display: none;
  }
}
</style>
